import { Component, Suspense, lazy } from 'react';

// Critical CSS - loaded immediately (contains fonts and CSS variables)
import './theme/app.css';

// Non-critical CSS will be loaded dynamically after initial render
// This ensures they're in separate chunks and can be deferred

import { Route, Routes, BrowserRouter, useParams } from 'react-router-dom';
import { connect } from 'react-redux';

import routes from './utils/routes';
import languages from './languages';

// const logo = '/assets/logo.webp';
const logo = '/assets/logo_70x50.webp';


import { AdminOnly, RequiresLogin, RequiresLogout } from '@acri-tools/react-tools/utils/routing';
import { on_app_did_update, on_app_mount, on_app_unmount } from '@acri-tools/react-tools';
import { LanguagesState } from '@acri-tools/react-tools/redux/reducers/languagesReducer';
import { LoginState } from '@acri-tools/react-tools/redux/reducers/loginReducer';
import { RTAppState } from '@acri-tools/react-tools/redux/reducers/RTAppReducer';

/** =========================================
 *  -- PAGES
    ========================================= */
import Menu from '@acri-tools/react-tools/components/Menu/Menu';

import Toasts from '@acri-tools/react-tools/components/Toasts/Toasts';

// Minimal history wrapper for on_app_mount compatibility
// This wrapper satisfies the history interface for react-tools library
// Navigation is handled by React Router v6, but this allows react-tools to work
const createMinimalHistory = () => {
    const updateLocation = () => ({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        state: null,
        key: undefined
    });

    let currentLocation = updateLocation();
    const listeners: Array<(location: any, action: string) => void> = [];

    // Update location when browser navigation occurs
    const handlePopState = () => {
        currentLocation = updateLocation();
        listeners.forEach(listener => listener(currentLocation, 'POP'));
    };
    window.addEventListener('popstate', handlePopState);

    return {
        length: window.history.length,
        action: 'POP' as const,
        get location() {
            return updateLocation();
        },
        push: (path: string, state?: any) => {
            // Use window.history.pushState and dispatch popstate to trigger React Router
            window.history.pushState(state, '', path);
            currentLocation = updateLocation();
            // Dispatch popstate event so React Router picks it up
            window.dispatchEvent(new PopStateEvent('popstate', { state }));
            listeners.forEach(listener => listener(currentLocation, 'PUSH'));
        },
        replace: (path: string, state?: any) => {
            // Use window.history.replaceState and dispatch popstate to trigger React Router
            window.history.replaceState(state, '', path);
            currentLocation = updateLocation();
            // Dispatch popstate event so React Router picks it up
            window.dispatchEvent(new PopStateEvent('popstate', { state }));
            listeners.forEach(listener => listener(currentLocation, 'REPLACE'));
        },
        go: (n: number) => window.history.go(n),
        goBack: () => window.history.back(),
        goForward: () => window.history.forward(),
        listen: (listener: (location: any, action: string) => void) => {
            listeners.push(listener);
            // Return unsubscribe function
            return () => {
                const index = listeners.indexOf(listener);
                if (index > -1) {
                    listeners.splice(index, 1);
                }
            };
        },
        block: (prompt?: boolean | string | ((location: any, action: string) => string | boolean)) => {
            // Return a no-op unblock function
            return () => {};
        },
        createHref: (location: any) => {
            return location.pathname + (location.search || '') + (location.hash || '');
        },
        createURL: (location: any) => new URL(location.pathname, window.location.origin),
        encodeLocation: (location: any) => location
    };
};

import Footer from './components/Footer/Footer';
import LoadingPage from './pages/LoadingPage/LoadingPage';

// Lazy load Admin component (must be outside component to prevent recreation)
const Admin = lazy(() => import('@acri-tools/react-tools/pages/Admin/Admin'));

// Admin wrapper component to provide match prop for React Router 6 compatibility
const AdminWrapper = () => {
    const params = useParams<{ selected?: string; subSelected?: string }>();
    return <Admin match={{ params } as any} />;
};

// Route-based code splitting configuration
interface RouteConfig {
    path: string;
    element: React.LazyExoticComponent<any> | React.ComponentType<any>;
    requiresLogin?: boolean;
    requiresLogout?: boolean;
    adminOnly?: boolean;
}

const routeConfigs: RouteConfig[] = [
    {
        path: '/admin/:selected?/:subSelected?',
        element: AdminWrapper,
        requiresLogin: true,
        adminOnly: true,
    },
    {
        path: '/',
        element: lazy(() => import('./pages/Home/Home')),
    },
    {
        path: '/login',
        element: lazy(() => import('./pages/Login/Login')),
        requiresLogout: true,
    },
    {
        path: '/account',
        element: lazy(() => import('./pages/Account/Account')),
        requiresLogin: true,
    },
    {
        path: '/register',
        element: lazy(() => import('./pages/Register/Register')),
        requiresLogout: true,
    },
    {
        path: '/verify-registration/:token',
        element: lazy(() => import('./pages/VerifyRegistration/VerifyRegistration')),
        requiresLogout: true,
    },
    {
        path: '/forgot-password',
        element: lazy(() => import('./pages/ForgotPassword/ForgotPassword')),
        requiresLogout: true,
    },
    {
        path: '/update-password/:token',
        element: lazy(() => import('./pages/UpdatePassword/UpdatePassword')),
    },
    {
        path: '/update-email/:token',
        element: lazy(() => import('./pages/UpdateEmail/UpdateEmail')),
    },
    {
        path: '/news',
        element: lazy(() => import('./pages/Blog/Blog')),
    },
    {
        path: '/news/:id',
        element: lazy(() => import('./pages/BlogPage/BlogPage')),
    },
    {
        path: '/project',
        element: lazy(() => import('./pages/Project/Project')),
    },
    {
        path: '/project/objectives',
        element: lazy(() => import('./pages/Objectives/Objectives')),
    },
    {
        path: '/project/tasks',
        element: lazy(() => import('./pages/Tasks/Tasks')),
    },
    {
        path: '/project/outcomes-and-deliverables',
        element: lazy(() => import('./pages/OutcomesAndDeliverables/OutcomesAndDeliverables')),
    },
    {
        path: '/contact',
        element: lazy(() => import('./pages/Contact/Contact')),
    },
    {
        path: '/team',
        element: lazy(() => import('./pages/Team/Team')),
    },
    {
        path: '/team/collaborators',
        element: lazy(() => import('./pages/Collaborators/Collaborators')),
    },
    {
        path: '/team/consortium',
        element: lazy(() => import('./pages/Consortium/Consortium')),
    },
    {
        path: '/related-links',
        element: lazy(() => import('./pages/RelatedLinks/RelatedLinks')),
    },
    {
        path: '*',
        element: lazy(() => import('./pages/NotFound/NotFound')),
    },
];

import { GDPR_LINK } from './utils/config';

// Lazy load GDPRBanner component
const GDPRBanner = lazy(() => import('@acri-tools/react-tools/components/GDRPBanner/GDPRBanner'));

// Wrapper component for lazy-loaded GDPRBanner with Suspense
const GDPRBannerWrapper = (props: any) => (
    <Suspense fallback={null}>
        <GDPRBanner {...props} />
    </Suspense>
);

// Helper function to render route element with appropriate wrappers
const renderRouteElement = (config: RouteConfig) => {
    const { element: Element, requiresLogin, requiresLogout, adminOnly } = config;
    
    // Render the element (works for both lazy components and regular components)
    let wrappedElement: JSX.Element = <Element />;
    
    // Apply wrappers in order: requiresLogout -> requiresLogin -> adminOnly
    if (requiresLogout) {
        wrappedElement = <RequiresLogout>{wrappedElement}</RequiresLogout>;
    }
    if (requiresLogin) {
        wrappedElement = <RequiresLogin>{wrappedElement}</RequiresLogin>;
    }
    if (adminOnly) {
        wrappedElement = <AdminOnly>{wrappedElement}</AdminOnly>;
    }
    
    return wrappedElement;
};

// Create minimal history object for on_app_mount compatibility
// React Router v6 handles navigation, this is just for the react-tools library
const history = createMinimalHistory();


interface Props {
    // ======== REDUX =========
    rt_app: RTAppState
    languages: LanguagesState
    login: LoginState
}
interface State {
}

export default connect((state: any) => {
    return {
        login: state.login,
        languages: state.languages,
        rt_app: state.rt_app
    }
})(class App extends Component<Props, State> {
    componentDidMount() {
        // Dynamically import non-critical CSS after initial render
        // This creates separate CSS chunks that can be loaded asynchronously
        const loadNonCriticalCSS = async () => {
            try {
                await Promise.all([
                    import('@fortawesome/fontawesome-free/css/all.min.css'),
                    import('@acri-tools/react-tools/css/all.css')
                ]);
            } catch (error) {
                console.warn('Failed to load non-critical CSS:', error);
            }
        };

        // Defer CSS loading until after initial render
        if (typeof window !== 'undefined') {
            if ('requestIdleCallback' in window) {
                requestIdleCallback(loadNonCriticalCSS, { timeout: 2000 });
            } else {
                setTimeout(loadNonCriticalCSS, 100);
            }
        }
        on_app_mount(history, {
            rt_app: {
                modules: {
                    article_manager: true,
                    file_manager: true
                },
                GAHosts: {
                    "4dbaltdyn.dev.acrist-services.com": "G-F6ZKC6TMX5",
                    "4dbaltdyn.albavalor.es": "G-WNF8B8QYYL"
                },
                languageInParams: false
            },
            routing: {
                routes,
            },
            layout: {
                logo,
                footer: <Footer />,
                gdpr: <GDPRBannerWrapper
                    last_update={"2023-04-13T05:30:00Z"}
                    link={GDPR_LINK}
                    consent_options={{
                        "ga": "I give consent to have my activity tracked on Google Analytics to help participate in the development of this website"
                    }}
                />,
                admin: {
                    darkMode: false,
                },
            },
            languagesInit: {
                languages,
                default: 'en'
            },
            article_manager: {
                options: {
                    categories: [
                        'Feature articles', 'Interviews with researchers', 'Detailed reports'
                    ]
                },
                articles_url: '/news',
                article_url: '/news',
            },
        });

    }
    componentWillUnmount() {
        on_app_unmount();
    }

    componentDidUpdate(prevProps: Props) {
        on_app_did_update(prevProps, this.props);
    }

    render() {
        return (
            <div id="app">
                {
                    !this.props.rt_app.initialized ?
                        <LoadingPage />
                        :
                        <BrowserRouter>
                            <Menu />
                            <Toasts />
                            <main>
                                <Suspense fallback={<LoadingPage />}>
                                    <Routes>
                                        {routeConfigs.map((config) => (
                                            <Route
                                                key={config.path}
                                                path={config.path}
                                                element={renderRouteElement(config)}
                                            />
                                        ))}
                                    </Routes>
                                </Suspense>
                            </main>
                        </BrowserRouter>
                }
            </div>
        )
    }
})